import { MarketplaceEligibleFundingOrganismType } from '../../utils/sharedTypes'

// Visuels produits déjà publics sur S3 et déjà utilisés ailleurs dans l'app (PreInscription,
// Header, Drawer). Tous les produits n'en ont pas : les composants gèrent l'échec de chargement.
const S3_FORMATIONS_BASE = 'https://walter-resources.s3.eu-west-3.amazonaws.com/formations'

export const productCardImageUrl = (productCode: string): string =>
  `${S3_FORMATIONS_BASE}/${productCode.toLowerCase()}/img2/card_w255_h410.jpeg`

/** Logotype sur fond sombre (incrusté sur l'image de card). */
export const productLogotypeUrl = (productCode: string): string =>
  `${S3_FORMATIONS_BASE}/${productCode.toLowerCase()}/img2/logotype.svg`

/** Logotype sur fond clair (modale, listes). */
export const productLogotypeBlackUrl = (productCode: string): string =>
  `${S3_FORMATIONS_BASE}/${productCode.toLowerCase()}/img2/logotype_black.svg`

const euroFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

export const formatPrice = (price: number | null | undefined): string | null =>
  price === null || price === undefined ? null : euroFormatter.format(price)

export const formatDuration = (hours: number | null | undefined): string | null =>
  hours === null || hours === undefined ? null : `${hours} h`

// En dessous de ce nombre d'avis, la moyenne n'est pas représentative : mieux vaut ne rien afficher
// que d'exposer un « 5,0 » adossé à deux votes, ce qui décrédibilise les vraies notes du catalogue.
const MIN_CREDIBLE_REVIEWS = 10

/** Note et nombre d'avis, ou `null` si l'échantillon est trop faible pour être affiché. */
export const formatRating = (
  average: number | null | undefined,
  count: number | null | undefined,
): { label: string; detailedLabel: string } | null => {
  if (average === null || average === undefined) return null
  if (!count || count < MIN_CREDIBLE_REVIEWS) return null
  return {
    label: `${average.toFixed(1)} (${count})`,
    detailedLabel: `${average.toFixed(1)} · ${count} avis`,
  }
}

export type BudgetEnvelopeType = { min: number; max: number; unit: 'eur' | 'hours' | undefined }

/** Enveloppe annuelle portée par un financeur, quelle que soit la forme reçue.
 *
 * Le parcours identifié reçoit le montant exact du couple (financeur, profession) ; le parcours
 * anonyme n'en reçoit qu'une fourchette, calculée sur toutes les professions du financeur. Les deux
 * se lisent ici de la même façon, pour que rien en aval n'ait à savoir d'où vient le payload. */
export const organismEnvelope = (organism: MarketplaceEligibleFundingOrganismType): BudgetEnvelopeType | null => {
  if (organism.annual_budget !== undefined) {
    return { min: organism.annual_budget, max: organism.annual_budget, unit: organism.unit }
  }
  if (organism.annual_budget_min !== undefined && organism.annual_budget_max !== undefined) {
    return { min: organism.annual_budget_min, max: organism.annual_budget_max, unit: organism.unit }
  }
  return null
}

/** Enveloppe théorique annuelle du financeur, ex. « jusqu'à 21 h/an ». */
export const formatBudgetLabel = (organism: MarketplaceEligibleFundingOrganismType): string | null => {
  const envelope = organismEnvelope(organism)
  if (!envelope) return null
  return formatBudgetRangeLabel(envelope.min, envelope.max, envelope.unit)
}

export const organismKey = (organism: MarketplaceEligibleFundingOrganismType): string => organism.code || organism.name

/** Enveloppe d'un financeur sur un ensemble de thèmes. Le montant dépendant de la profession, il
 * varie d'un thème à l'autre au sein d'un même financeur : on annonce alors une fourchette plutôt
 * qu'un chiffre unique, qui contredirait les badges des cards. */
export function formatBudgetRangeLabel(min: number, max: number, unit: 'eur' | 'hours' | undefined): string {
  const unitLabel = unit === 'hours' ? 'h' : '€'
  if (Math.round(min) === Math.round(max)) return `jusqu'à ${Math.round(min)} ${unitLabel}/an`
  return `de ${Math.round(min)} à ${Math.round(max)} ${unitLabel}/an selon votre profession`
}
